import { useState, useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { toast } from 'react-toastify';

import { Tooltip, Box, TableCell, CircularProgress } from '@mui/material';

// api
import RoomApi from '../../api/room.api';
import BuildingApi from '../../api/building.api';

// comps
import GlobalButton from '../../components/global/button';
import GlobalDropdown from '../../components/global/dropdown';
import GlobalInput from '../../components/global/input';
import FilterContainer from '../../components/layout/filterContainer';
import ContentContainer from '../../components/layout/contentContainer';
import DataGrid from '../../components/grid/dataGrid';
import FilterOption from '../../components/global/filterOption';
import { CustomTableInputCell, CustomTableDropdownCell, CustomTableCheckboxCell } from '../../components/grid/CustomTableCell';

// icons
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter-icon.svg';

// constants
import { colors } from '../../theme';
import { useAppSelector } from '../../hooks/hooks';
import { isUserTypeCentralTimetabler, isUserTypeFacilities } from '../../helpers/user';
import { DTC_CAMPUS_ID } from '../../constants';

const RoomConfiguration = (props: any) => {
    const _roomApi = new RoomApi();
    const _buildingApi = new BuildingApi();

    const appState = useAppSelector((state) => state.appState);
    const user = useAppSelector((state) => state.user);

    const isUserCentralTimetabler = isUserTypeCentralTimetabler(user, appState.viewType);
    const isUserFacilities = isUserTypeFacilities(user, appState.viewType);

    const [resetPage, setResetPage] = useState(false);
    const [state, setState] = useState({
        filterObject: {
            roomType: {
                id: null,
                name: '',
            },
            roomNumber: '',
            desc: '',
            config: '',
            active: 'all',
        },
        roomData: [],
        roomTypes: [],
        buildings: [],
        isClearAll: false,
    });

    const [isLoading, setIsLoading] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [selected, setSelected] = useState({
        building: null,
    });

    const loadData = async (campusId?: number) => {
        const buildings = await _buildingApi.GetAllBuildings(campusId);
        let selectedBuilding = null;

        //Auto select if there is only one building. The building dropdown will be hidden
        if (buildings.length === 1) {
            selectedBuilding = buildings[0];
        }

        const roomTypes = await _roomApi.GetRoomTypes();

        setState({
            ...state,
            roomTypes,
            buildings,
            roomData: [],
        });

        setSelected({
            ...selected,
            building: selectedBuilding,
        });
    };

    useEffect(() => {
        loadData(appState.campus?.id).catch((error) => toast.error('Error loading data'));
    }, [appState.campus]);

    const loadRoomData = async (buildingId: number) => {
        setIsLoading(true);
        const roomData = await _roomApi.GetRooms(buildingId);

        setState({
            ...state,
            roomData,
        });
    };

    useEffect(() => {
        if (selected.building) {
            loadRoomData(selected.building.id)
                .catch((error) => toast.error('Error loading buildings'))
                .finally(() => setIsLoading(false));
        }
    }, [selected.building]);

    const filterActiveItems = [
        { id: 1, label: 'All', value: 'all' },
        { id: 2, label: 'Active', value: true },
        { id: 3, label: 'Inactive', value: false },
    ];

    const handleFilter = (name: any, value: any) => {
        setState({
            ...state,
            filterObject: {
                ...state.filterObject,
                [name]: value,
            },
        });
        setButtonDisabled(false);
    };

    const handleSelectIsActive = (isActive: any) => {
        setState({
            ...state,
            filterObject: {
                ...state.filterObject,
                active: isActive,
            },
        });
        setButtonDisabled(false);
    };

    const handleClearAll = () => {
        setState({
            ...state,
            isClearAll: true,
            filterObject: {
                active: 'all',
                roomNumber: '',
                roomType: {
                    id: null,
                    name: '',
                },
                desc: '',
                config: '',
            },
        });
        setResetPage(true);
        setButtonDisabled(true);
    };

    useEffect(() => {
        if (!Object.values(state.filterObject).every((o) => o === '')) {
            setResetPage(true);
        } else {
            setResetPage(false);
            setButtonDisabled(true);
        }
    }, [state.filterObject]);

    useEffect(() => {
        setState({ ...state, isClearAll: false });
    }, [state.isClearAll]);

    const getGridData = () => {
        let roomData = [...state.roomData];
        const { roomType, roomNumber, active, desc, config } = state.filterObject;

        if (roomType.id) roomData = roomData.filter((room) => room.roomType?.id === roomType?.id);
        if (roomNumber) roomData = roomData.filter((room) => room.number.startsWith(roomNumber));
        if (desc) roomData = roomData.filter((room) => room.description?.toLowerCase().includes(desc.toLowerCase()));
        if (config) roomData = roomData.filter((room) => room.configuration?.toLowerCase().includes(config.toLowerCase()));
        if (active !== 'all') roomData = roomData.filter((room) => room.isActive === active);

        return roomData;
    };

    const updateRoom = async (room: any): Promise<boolean> => {
        // loadRoomData(selected.building.id);
        try {
            await _roomApi.UpdateRoom(room);
            toast.success(`Changes to room ${room.number} has been successfully saved.`);
            return true;
        } catch (error) {
            toast.error('Error updating room');
            return false;
        }
    };

    const renderRoomGrid = (data: any, onChange: any) => {
        return (
            <>
                <TableCell sx={{ color: colors.blue.main, fontWeight: 500, width: '8%' }}>{data.number}</TableCell>

                {/* Room Type Cell */}
                <CustomTableDropdownCell
                    {...{ data, items: state.roomTypes, onChange, width: '17%', value: data.roomType?.name }}
                    name={'name'}
                    field={'id'}
                    placeholder={data.roomType?.name}
                    dataItemKey={'roomType'}
                />

                <CustomTableInputCell {...{ data, name: 'capacity', onChange, sx: { width: '10%' }, type: 'number', parse: 'int', id: 'id' }} center />
                <CustomTableInputCell {...{ data, name: 'description', id: 'id', onChange, sx: { width: 180 } }} />
                <CustomTableInputCell {...{ data, name: 'configuration', id: 'id', onChange, sx: { width: 260 } }} />
                <CustomTableCheckboxCell {...{ data, name: 'isActive', id: 'id', onChange, disabled: false }} />
            </>
        );
    };

    const handleBuildingChange = (name: any, value: any) => {
        setSelected({ ...selected, building: value });
    };

    return (
        <>
            <FilterContainer sx={{ justifyContent: 'space-between' }}>
                <FlexBox>
                    {/* No need to show the building dropdown if there is only one building */}
                    {state.buildings.length > 1 && (
                        <GlobalDropdown
                            label="Building"
                            value={selected.building}
                            data={state.buildings}
                            placeholder="Select..."
                            name="name"
                            field="id"
                            width="130px"
                            dataItemKey={'building'}
                            onChange={handleBuildingChange}
                            sx={{ mr: '10px' }}
                            isSearch
                        />
                    )}
                    <GlobalDropdown
                        label="filter"
                        placeholder="Room Types"
                        icon={<FilterIcon />}
                        data={state.roomTypes}
                        name={'name'}
                        field={'id'}
                        dataItemKey={'roomType'}
                        onChange={handleFilter}
                        value={state.filterObject?.roomType}
                        sx={{ mr: '5px' }}
                        width="150px"
                    />
                    <GlobalInput placeholder={'Room #'} name={'roomNumber'} value={state.filterObject?.roomNumber} onChange={handleFilter} sx={{ width: '80px', mr: '5px' }} />
                    <GlobalInput placeholder={'Description'} name={'desc'} value={state.filterObject?.desc} onChange={handleFilter} sx={{ width: '150px', mr: '5px' }} />
                    <GlobalInput placeholder={'Configuration'} name={'config'} value={state.filterObject?.config} onChange={handleFilter} sx={{ width: '150px', mr: '10px' }} />
                    <FilterOption data={filterActiveItems} state={state.filterObject.active} name={'label'} onClickValue={'value'} onOptionClick={handleSelectIsActive} />
                </FlexBox>
                <Box sx={{ width: '10%' }}>
                    <GlobalButton variant="round" text="clear all" fullWidth secondary={'true'} endIcon={<RefreshIcon />} onClick={handleClearAll} disabled={buttonDisabled} />
                </Box>
            </FilterContainer>
            {state.roomData?.length === 0 && isLoading ? (
                <ContentContainer padding={'0px 25px'}>
                    <FlexBox sx={{ justifyContent: 'center', height: '100%' }}>
                        <CircularProgress size={100} />
                    </FlexBox>
                </ContentContainer>
            ) : (
                <DataGrid
                    isEdit={isUserCentralTimetabler ? appState.campus?.id !== DTC_CAMPUS_ID : isUserFacilities ? appState.campus?.id === DTC_CAMPUS_ID : false}
                    resetPage={resetPage}
                    topHeight={20}
                    data={getGridData()}
                    columns={roomGridColumns}
                    onSaveAsync={updateRoom}
                    renderRows={renderRoomGrid}
                    dataItemKey="id"
                    isClearSorting={state.isClearAll}
                    onHeaderClick={() => {
                        setButtonDisabled(false);
                    }}
                />
            )}
        </>
    );
};

export default RoomConfiguration;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: center;
    }
`;

const roomGridColumns = [
    {
        id: 'number',
        label: 'Room #',
    },
    {
        label: 'Type',
        disabled: true,
    },
    {
        id: 'capacity',
        label: 'Capacity',
        sx: {
            textAlign: 'center',
        },
    },
    {
        label: 'Description',
        disabled: true,
    },
    {
        label: 'Configuration',
        disabled: true,
    },
    {
        label: 'Active',
        sx: {
            textAlign: 'center',
        },
        isTooltip: true,
        tooltipText: 'Hide/Show room(s) from Daily Grid/Room Search',
        disabled: true,
    },
];
